import { useGT } from 'gt-react';

import type { DailyReportKind, FlagReasonId } from '@/lib/daily-reports/types';

export function useDailyReportKindLabel(): (
  kind: Exclude<DailyReportKind, 'unknown'>,
) => string {
  const gt = useGT();
  const labels = {
    suspendedItem: gt('Suspended Item', {
      context: 'name of the Rexel ITEMSUSPE4 daily report',
    }),
    supersede: gt('Daily supersede', {
      context: 'name of the Rexel daily supersede report',
    }),
    potfUpc: gt('POTF items with UPC codes', {
      context: 'name of the Rexel POTF UPC daily report',
    }),
    vendorsCreated: gt('Vendors created the previous day', {
      context: 'name of the Rexel new-vendor daily report',
    }),
    webNoPrice: gt('Web-flagged item with no price record', {
      context: 'name of the Rexel WEBNOPRF daily report',
    }),
    supersededWithStock: gt('Superseded item with stock records', {
      context: 'name of the Rexel STOCKS daily report',
    }),
    rdStockWithoutItem: gt('RD stock with no matching item master', {
      context: 'name of the Rexel STOCK_V daily report',
    }),
    vendorItemList: gt('Vendor item list', {
      context: 'name of the Rexel vendor item list daily report',
    }),
  };
  return (kind) => labels[kind];
}

export function useFlagReasonLabel(): (id: FlagReasonId) => string {
  const gt = useGT();
  const labels = {
    warehouseLocation: gt('Warehouse location', {
      context: 'Suspended Item flag when WRH LOCATION has text',
    }),
    overstockWarehouseLocation: gt('Overstock warehouse location', {
      context: 'Suspended Item flag when OVERSTK WRH LOCATION has text',
    }),
    stockFlagY: gt('Stock flag Y', {
      context: 'Suspended Item flag when STK Y/N is Y',
    }),
    qtyOnHand: gt('Qty on hand', {
      context: 'Suspended Item flag when QTY ON HAND is non-zero',
    }),
    onOrderQty: gt('On-order qty', {
      context: 'Suspended Item flag when ON-ORD QTY is non-zero',
    }),
    allocQty: gt('Allocated qty', {
      context: 'Suspended Item flag when ALLOC QTY is non-zero',
    }),
    backorderQty: gt('Backorder qty', {
      context: 'Suspended Item flag when B/O QTY is non-zero',
    }),
    poNumber: gt('Open PO', {
      context: 'Suspended Item flag when P/O# is non-zero',
    }),
    orderNumber: gt('Open sales order', {
      context: 'Suspended Item flag when ORDER NUMBER is non-zero',
    }),
    unmappedStockLocation: gt('No item-master partner', {
      context:
        'STOCK_V flag when the stock location letter is not in the RD DC map',
    }),
    unmappedItemMasterLocation: gt('Unknown item-master location', {
      context: 'STOCK_V flag when the item-master letter is not a Vera RD pair',
    }),
    unpairedItemMasterLocation: gt('Unpaired item-master I', {
      context: 'STOCK_V flag when item-master location I has no stock partner',
    }),
    stockItemLocationMismatch: gt('Stock/item locations do not correspond', {
      context:
        'STOCK_V flag when stock and item-master letters are not a Vera pair',
    }),
    nonRdDivision: gt('Not RD company/division', {
      context:
        'STOCK_V flag when stock access company+division is not Rexel RD',
    }),
    rdVeraPair: gt('Same-DC stock/item-master pair', {
      context:
        'ITEMSUSPE4 RD flag when STOCK LOC and ITEM LOC are a Vera DC pair',
    }),
    webFlaggedNoPrice: gt('Web-flagged, no price record', {
      context: 'WEBNOPRF worklist reason for a product row on that report',
    }),
    potfUpcPresent: gt('POTF item with UPC', {
      context: 'POTF_UPC worklist reason for a product row on that report',
    }),
    pipeInDescription: gt('Pipe in description', {
      context:
        'VITMLISTER flag when English or French description contains a pipe',
    }),
  };
  return (kind) => labels[kind];
}
